import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import axios from 'axios'; // Importeer axios direct voor de externe API call
import { Loader2, TrendingUp, TrendingDown, AlertCircle } from 'lucide-react';

// ... (Interface StockData blijft hetzelfde) ...
interface StockData {
    symbol: string;
    price: number;
    change: number;
    changePercent: number;
    name?: string; // Optionele naam
}

// Interface voor de widget props (incl. settings)
interface StockMarketWidgetProps {
    id: string;
    title: string;
    symbols?: string[]; // Symbolen om te tonen, uit settings
}

// API configuration
const STOCK_API_KEY = import.meta.env.VITE_ALPHAVANTAGE_API_KEY;
const API_ENDPOINT = 'https://www.alphavantage.co/query';

// Destructure props correctly
export const StockMarketWidget: React.FC<StockMarketWidgetProps> = ({ id, title, symbols = [] }) => {
    // ... (useState hooks blijven hetzelfde) ...
    const [stockData, setStockData] = useState<StockData[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchStockData = async () => {
            if (!STOCK_API_KEY) {
                setError('API sleutel ontbreekt. Stel VITE_ALPHAVANTAGE_API_KEY in .env in.');
                setIsLoading(false);
                return;
            }
             if (!symbols || symbols.length === 0) { // Controleer ook of symbols leeg is
                 setError('Geen aandeelsymbolen geconfigureerd.');
                 setIsLoading(false);
                 return;
            }

            setIsLoading(true);
            setError(null);
            setStockData([]);

            try {
                // Maak API calls voor elk symbool
                const promises = symbols.map((symbol: string) => {
                    const url = `${API_ENDPOINT}?function=GLOBAL_QUOTE&symbol=${symbol}&apikey=${STOCK_API_KEY}`;
                    return axios.get(url); // Gebruik axios.get
                });

                const responses = await Promise.all(promises);

                const fetchedData: StockData[] = responses.map((response: any, index: number) => {
                    // Controleer op API limiet of fouten
                    if (response.data.Note) {
                         console.warn(`Alpha Vantage API Note voor ${symbols[index]}: ${response.data.Note}`);
                         // Je kunt hier kiezen om een fout te gooien of door te gaan
                         // throw new Error(`API Limiet bereikt voor ${symbols[index]}`);
                    }
                    if (!response.data || !response.data['Global Quote']) {
                        throw new Error(`Ongeldige response ontvangen voor symbool ${symbols[index]}`);
                    }

                    const quote = response.data['Global Quote'];

                    // Controleer of de essentiële velden bestaan
                    if (!quote['01. symbol'] || quote['05. price'] === undefined || quote['09. change'] === undefined || quote['10. change percent'] === undefined) {
                       throw new Error(`Onvolledige data ontvangen voor symbool ${symbols[index]}`);
                    }

                    return {
                        symbol: quote['01. symbol'],
                        price: parseFloat(quote['05. price']),
                        change: parseFloat(quote['09. change']),
                        changePercent: parseFloat(quote['10. change percent'].replace('%', '')), // Verwijder % teken
                        // Optioneel: Haal naam op als die beschikbaar is (niet standaard in GLOBAL_QUOTE)
                    };
                }).filter(Boolean); // Verwijder eventuele null/undefined resultaten door errors

                setStockData(fetchedData);

            } catch (err: any) {
                console.error("Error fetching stock data from Alpha Vantage:", err);
                 // Toon specifiekere foutmelding als de API die geeft
                let errorMessage = `Fout bij ophalen data: ${err.message || 'Onbekende fout'}`;
                if (err.response?.data?.Information) { // Specifieke Alpha Vantage error
                     errorMessage = `API Fout: ${err.response.data.Information}`;
                }
                setError(errorMessage);
            } finally {
                setIsLoading(false);
            }
        };

        fetchStockData();
        // ... (Optioneel: Interval) ...

    }, [symbols]); // Dependency array uses the destructured symbols

   // ... (Helper functies formatPrice, formatChange, formatChangePercent blijven hetzelfde) ...
   const formatPrice = (price: number): string => {
        return price.toFixed(2);
    }

    const formatChange = (change: number): string => {
         return `${change > 0 ? '+' : ''}${change.toFixed(2)}`;
    }

    const formatChangePercent = (percent: number): string => {
        return `${percent > 0 ? '+' : ''}${percent.toFixed(2)}%`;
    }

    // ... (JSX Rendering blijft grotendeels hetzelfde) ...
     return (
        <Card className="h-full flex flex-col">
            {/* ... (CardHeader) ... */}
             <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">{title}</CardTitle>
            </CardHeader>
            <CardContent className="flex-1 overflow-y-auto">
                {isLoading ? (
                     <div className="flex justify-center items-center h-full">
                        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    </div>
                // ... (Error en Geen Data weergave) ...
                 ) : error ? (
                    <div className="flex flex-col justify-center items-center h-full text-destructive text-sm">
                         <AlertCircle className="h-5 w-5 mb-1" />
                         <span>{error}</span>
                    </div>
                ) : stockData.length === 0 ? (
                     <div className="flex justify-center items-center h-full text-muted-foreground text-sm">
                         Geen data beschikbaar.
                    </div>
                // ... (Stock data mapping) ...
                 ) : (
                    <div className="space-y-3">
                        {stockData.map((stock) => (
                            <div key={stock.symbol} className="flex justify-between items-center border-b border-border pb-2 last:border-0 last:pb-0">
                                <div>
                                    <p className="text-sm font-semibold text-foreground">{stock.symbol}</p>
                                    {stock.name && <p className="text-xs text-muted-foreground">{stock.name}</p>}
                                </div>
                                <div className="text-right">
                                    <p className="text-sm font-medium text-foreground">
                                        ${formatPrice(stock.price)}
                                    </p>
                                    <p className={`text-xs ${stock.change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                        {stock.change >= 0 ? <TrendingUp className="h-3 w-3 inline mr-1" /> : <TrendingDown className="h-3 w-3 inline mr-1" />}
                                        {formatChange(stock.change)} ({formatChangePercent(stock.changePercent)})
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </CardContent>
        </Card>
    );
}; 